import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Row, Col } from "react-bootstrap";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";
import FileUploadComponent from "../common-components/FileUploadComponent";
import { selectToken } from "../auth/auth.selector";
import {
  commonPostPutApi,
  fetchApiData,
} from "../../redux/reducers/commonApiSlice";
import "../../styles/ticket-system/ticket-details-modal.css";
import "../../styles/ticket-system/ticket-status-badges.css";
import { replaceUnderscores } from "./utils/formatStatus";
import {
  ticketSystemResolve,
  ticketSystemVerify,
} from "../../data/ApiEndpoints";
import { formatDateTime } from "../common-data/Converts";
import ModalHeadAndCloseV2 from "../payouts-wtf/common/ModalHeadAndCloseV2";
import { fetchSupervisors } from "../../redux/reducers/ticketSystemSlice";
import TicketLifecycleTimeline from "./TicketLifecycleTimeline";
import {
  TICKET_OPENED,
  TICKET_REOPENED,
  TICKET_CLOSED,
} from "./utils/ticketStatus";

const TicketDetailsModal = ({
  isOpen,
  onClose,
  ticket,
  onTicketUpdate,
  userInfoData,
  onWriteSuccess,
  scrollToVerification = false,
  onScrollComplete,
}) => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);

  // Helper function to determine assignment status - same logic as TicketListing
  const getAssignmentStatus = (ticket) => {
    const hasAssignedTo =
      ticket?.assigned_to && ticket.assigned_to.trim() !== "";
    const hasAssignedToName =
      ticket?.assigned_to_name && ticket.assigned_to_name.trim() !== "";

    if (!hasAssignedTo && !hasAssignedToName) {
      // First time assignment - both are null/empty
      return { isFirstTime: true, isReassignment: false };
    } else if (hasAssignedTo && !hasAssignedToName) {
      // User was assigned but deleted/left - has ID but no name
      return { isFirstTime: false, isReassignment: true };
    } else if (hasAssignedTo && hasAssignedToName) {
      // Normal reassignment - both have values
      return { isFirstTime: false, isReassignment: true };
    } else {
      // Edge case: has name but no ID (shouldn't happen normally)
      return { isFirstTime: true, isReassignment: false };
    }
  };

  const { supervisors } = useSelector((state) => state.ticketSystem);

  const [loading, setLoading] = useState(false);
  const [workflowLogs, setWorkflowLogs] = useState([]);
  const [resolutionDetails, setResolutionDetails] = useState(null);
  const [tatMetrics, setTatMetrics] = useState(null);
  const [ticketStats, setTicketStats] = useState(null);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [isSubmittingResolution, setIsSubmittingResolution] = useState(false);
  // Phase 1 cascade: when checked, also resolve this ticket's related (child) tickets.
  const [resolveChildren, setResolveChildren] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isHighlighted, setIsHighlighted] = useState(false);

  // Resolution proof media state
  const [resolutionFiles, setResolutionFiles] = useState({
    images: [],
    videos: [],
  });

  // Manager verification state
  const [verifiedRemarks, setVerifiedRemarks] = useState("");
  const [isSubmittingVerification, setIsSubmittingVerification] =
    useState(false);

  // Manager assignment state
  const [selectedSupervisor, setSelectedSupervisor] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);

  // Timeline modal state
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);

  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState({
    ticketStats: false,
    ticketOverview: false,
    attachments: false,
    workCompletionAttachments: false,
    tatMonitor: false,
    resolutionDetails: false,
    timeline: false,
    verification: false,
    managerVerification: false,
    assignment: false,
  });

  // Ref for verification section
  const verificationSectionRef = React.useRef(null);

  // Role checks
  const isManager = userInfoData?.ticket_account_type === "manager";
  const isSupervisor = userInfoData?.ticket_account_type === "supervisor";
  const isReceptionist = userInfoData?.ticket_account_type === "receptionist";
  const isAdmin = userInfoData?.ticket_account_type === "admin";

  // Toggle section collapse/expand
  const toggleSection = (sectionName) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  // Fetch ticket details and workflow logs
  useEffect(() => {
    if (isOpen && ticket?.ticket_no && token) {
      fetchTicketDetails();
      if (isManager) {
        fetchSupervisors();
        // Set default supervisor selection for reassignment
        if (ticket.assigned_to_name && ticket.assigned_to) {
          setSelectedSupervisor(ticket.assigned_to);
        }
      }
    }
  }, [isOpen, ticket?.ticket_no, token, isManager, ticket?.assigned_to]);

  const fetchTicketDetails = async () => {
    setLoading(true);
    try {
      // Fetch timeline from API
      const timelineResult = await dispatch(
        fetchApiData({
          endpoint: `/ticketSystem/timeline?ticket_id=${ticket.uid}`,
          token,
        })
      );
      // console.log("timelineResult", timelineResult);
      let timelineLogs = [];
      if (
        timelineResult?.payload?.status &&
        Array.isArray(timelineResult.payload.data)
      ) {
        timelineLogs = timelineResult.payload.data.map((item, idx) => ({
          id: item?.uid || idx,
          status: item?.action_type?.toUpperCase() || "ACTION",
          timestamp: item?.timeline_timestamp,
          actor: item?.performed_by_name || item?.performed_by || "Unknown",
          description: item?.remarks || "",
          statusColor: getTimelineStatusColor(item.action_type),
          actorAccountType: item?.performed_by_account_type || "Unknown",
        }));
      }
      setWorkflowLogs(timelineLogs);

      // Fetch TAT metrics from API
      const tatResult = await dispatch(
        fetchApiData({
          endpoint: `/ticketSystem/tat?ticket_id=${ticket.uid}`,
          token,
        })
      );

      if (tatResult?.payload?.status) {
        const tatData = tatResult.payload.data;
        setTatMetrics(tatData.tat_metrics);
      }
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      toast.error("Failed to load ticket details");
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketStats = async () => {
    try {
      // Get stats based on user role
      const userRole = userInfoData?.role?.toLowerCase() || "receptionist";
      const statsResult = await dispatch(
        fetchApiData({
          endpoint: `/ticketSystem/stats?stats_type=${userRole}`,
          token,
        })
      );

      if (statsResult?.payload?.status) {
        setTicketStats(statsResult.payload.data);
      }
    } catch (error) {
      console.error("Error fetching ticket stats:", error);
    }
  };

  useEffect(() => {
    if (isOpen && userInfoData?.role) {
      fetchTicketStats();
    }
  }, [isOpen, userInfoData?.role]);

  // Auto-scroll to verification section when modal opens with scrollToVerification flag
  useEffect(() => {
    if (isOpen && scrollToVerification) {
      // Add a small delay to ensure modal is fully rendered
      const timeoutId = setTimeout(() => {
        const verificationElement = verificationSectionRef.current;
        if (verificationElement) {
          console.log(
            "Scrolling to verification section...",
            verificationElement
          );

          // Try different scrolling approaches
          // Method 1: Find the modal content container and scroll within it
          const modalContent = document.querySelector(
            ".ticket-details-content"
          );
          if (modalContent) {
            const elementPosition =
              verificationElement.offsetTop - modalContent.offsetTop;
            modalContent.scrollTo({
              top: elementPosition - 20, // 20px offset for better visibility
              behavior: "smooth",
            });
            console.log("Scrolled within modal content container");
          } else {
            // Method 2: Fallback to regular scrollIntoView
            verificationElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
            console.log("Used scrollIntoView fallback");
          }

          // Add highlight effect after scrolling
          setTimeout(() => {
            setIsHighlighted(true);
            // Remove highlight after 3 seconds
            setTimeout(() => {
              setIsHighlighted(false);
            }, 3000);
          }, 600); // Wait for scroll to complete

          // Call onScrollComplete to reset the flag
          if (onScrollComplete) {
            onScrollComplete();
          }
        } else {
          console.log("Verification section ref not found");
        }
      }, 500); // Increased delay to 500ms for better reliability

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, scrollToVerification, onScrollComplete]);

  // Helper to get color for timeline status
  function getTimelineStatusColor(actionType) {
    switch ((actionType || "").toLowerCase()) {
      case "opened":
        return "#007bff";
      case "assigned":
        return "#6f42c1";
      case "work in progress":
        return "#fd7e14";
      case "work completed":
      case "resolved":
        return "#28a745";
      case "rejected":
        return "#dc3545";
      case "reopened":
        return "#ffc107";
      default:
        return "#6c757d";
    }
  }

  const handleTicketAction = async (action) => {
    // Validate required fields for resolution
    if (action === "resolved") {
      if (rating === 0) {
        toast.error("Please provide a rating");
        return;
      }
      if (!feedback.trim()) {
        toast.error("Please provide verification feedback");
        return;
      }
      if (
        resolutionFiles.images.length === 0 &&
        resolutionFiles.videos.length === 0
      ) {
        toast.error("Please upload at least one proof image or video");
        return;
      }
    }

    setIsSubmittingResolution(true);
    try {
      // Simulate API call for ticket actions
      const payload = {
        ticket_id: ticket?.uid,
        resolution_feedback: feedback,
        status: action,
        ...(action === "resolved" && {
          rating,
          resolved_by: userInfoData?.uid,
          imgarr: resolutionFiles.images,
          videoarr: resolutionFiles.videos,
          resolve_children: resolveChildren,
        }),

        ...(action === "rejected" && {
          rejected_by: userInfoData?.uid,
          imgarr: resolutionFiles.images,
          videoarr: resolutionFiles.videos,
        }),
      };

      const result = await dispatch(
        fetchApiData({
          method: "POST",
          endpoint: ticketSystemResolve,
          payload,
          token,
        })
      );

      // console.log("result===>", result);

      if (result?.payload?.status) {
        toast.success(result?.payload?.message);
        // fetchTicketDetails();
        onWriteSuccess();
        handleClose();
        // fetchTicketStatsFunc();
      } else {
        toast.error(result?.payload?.message || "Failed to resolve ticket");
      }

      setIsSubmittingResolution(false);
    } catch (error) {
      console.error(`Error ${action} ticket:`, error);
      toast.error(`Failed to ${action} ticket`);
      setIsSubmittingResolution(false);
    }
  };

  // Handle ticket assignment/reassignment
  const handleAssignTicket = async () => {
    if (!selectedSupervisor) {
      toast.error("Please select a supervisor");
      return;
    }

    // Use the helper function to determine assignment status
    const assignmentStatus = getAssignmentStatus(ticket);
    const isReassignment = assignmentStatus.isReassignment;
    const endpoint = isReassignment
      ? "/ticketSystem/reassign"
      : "/ticketSystem/assign";
    const payload = isReassignment
      ? {
          ticket_id: ticket?.uid,
          new_assigned_to: selectedSupervisor,
        }
      : {
          ticket_id: ticket?.uid,
          assigned_to: selectedSupervisor,
          assigned_by: userInfoData?.uid || userInfoData?.id,
        };

    setIsAssigning(true);
    try {
      const result = await dispatch(
        fetchApiData({
          method: "POST",
          endpoint,
          payload,
          token,
        })
      );

      if (result?.payload?.status) {
        toast.success(
          result?.payload?.message ||
            `Ticket ${isReassignment ? "reassigned" : "assigned"} successfully`
        );
        if (onWriteSuccess) onWriteSuccess();
        handleClose();
      } else {
        toast.error(
          result?.payload?.message ||
            `Failed to ${isReassignment ? "reassign" : "assign"} ticket`
        );
      }
    } catch (error) {
      console.error(
        `Error ${isReassignment ? "reassigning" : "assigning"} ticket:`,
        error
      );
      toast.error(`Failed to ${isReassignment ? "reassign" : "assign"} ticket`);
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle manager verification
  const handleManagerVerification = async (action) => {
    if (!verifiedRemarks.trim()) {
      toast.error("Please provide verification remarks");
      return;
    }

    setIsSubmittingVerification(true);

    try {
      const payload = {
        ticket_id: ticket?.uid,
        remarks: verifiedRemarks.trim(),
        verified_by: userInfoData?.uid || userInfoData?.id,
        status: action,
      };

      const result = await dispatch(
        fetchApiData({
          method: "POST",
          endpoint: ticketSystemVerify,
          payload,
          token,
        })
      );

      if (result?.payload?.status) {
        toast.success(result?.payload?.message);
        onWriteSuccess();
        handleClose();
      } else {
        toast.error(result?.payload?.message || "Failed to verify ticket");
      }
    } catch (error) {
      console.error("Error verifying ticket:", error);
      toast.error("Failed to verify ticket");
    } finally {
      setIsSubmittingVerification(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setFeedback("");
    setResolveChildren(false);
    setVerifiedRemarks("");
    setResolutionFiles({ images: [], videos: [] });
    setWorkflowLogs([]);
    setResolutionDetails(null);
    setTatMetrics(null);
    setIsHighlighted(false); // Reset highlight state
    // Reset assignment state
    setSelectedSupervisor("");
    setIsAssigning(false);
    // Reset collapsible sections
    setCollapsedSections({
      ticketStats: false,
      ticketOverview: false,
      attachments: false,
      workCompletionAttachments: false,
      tatMonitor: false,
      resolutionDetails: false,
      timeline: false,
      verification: false,
      managerVerification: false,
      assignment: false,
    });
    onClose();
  };

  const handleTimelineClick = () => {
    setIsTimelineModalOpen(true);
  };

  const handleTimelineModalClose = () => {
    setIsTimelineModalOpen(false);
  };

  // Open image in lightbox
  const openLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxImage(null);
  };

  // Handle resolution proof upload
  const handleResolutionFilesChange = (files) => {
    setResolutionFiles(files);
  };

  // console.log("ticket details", ticket);

  if (!ticket) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Ticket Details"
      ariaHideApp={false}
      closeTimeoutMS={300}
      className="ticket-details-modal"
      overlayClassName="ticket-details-overlay"
    >
      <div className="ticket-details-container">
        <div style={{ padding: "15px" }}>
          <ModalHeadAndCloseV2
            title={`Ticket Details - ${ticket?.ticket_no}`}
            onClose={handleClose}
          />
        </div>

        {loading ? (
          <div className="ticket-details-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading ticket details...</p>
          </div>
        ) : (
          <div className="ticket-details-content">
            {/* Status Badges */}
            <div className="ticket-status-badges">
              <span
                className={`ticket-system-status-badge ticket-system-status-badge-details status-${ticket.status?.toLowerCase()}`}
              >
                {replaceUnderscores(ticket.status)}
              </span>
              <span
                className={`ticket-system-status-badge 
                ticket-system-status-badge-details
                status-${ticket.priority?.toLowerCase()}`}
              >
                {replaceUnderscores(ticket.priority)} PRIORITY
              </span>
            </div>
            {ticketStats && (
              <div className="ticket-section ticket-stats-section">
                <div
                  className="section-header collapsible-header"
                  onClick={() => toggleSection("ticketStats")}
                >
                  <h3 className="section-title">
                    <span className="section-icon">📊</span>
                    Ticket Statistics
                  </h3>
                </div>

                {!collapsedSections.ticketStats && (
                  <div className="ticket-stats-grid">
                    <div className="stats-card">
                      <div className="stats-icon">📈</div>
                      <div className="stats-content">
                        <h4>Tickets This Month</h4>
                        <p className="stats-value">
                          {ticketStats.tickets_raised_this_month}
                        </p>
                      </div>
                    </div>

                    <div className="stats-card">
                      <div className="stats-icon">✅</div>
                      <div className="stats-content">
                        <h4>Tickets Closed</h4>
                        <p className="stats-value">
                          {ticketStats.tickets_closed}
                        </p>
                      </div>
                    </div>

                    <div className="stats-card">
                      <div className="stats-icon">🔄</div>
                      <div className="stats-content">
                        <h4>Tickets Reopened</h4>
                        <p className="stats-value">
                          {ticketStats.tickets_reopened}
                        </p>
                      </div>
                    </div>

                    <div className="stats-card">
                      <div className="stats-icon">⏱️</div>
                      <div className="stats-content">
                        <h4>Average Resolution Time</h4>
                        <p className="stats-value">
                          {ticketStats.avg_days > 0
                            ? `${ticketStats.avg_days}d `
                            : ""}
                          {ticketStats.avg_hours}h {ticketStats.avg_minutes}m
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Ticket Overview Section */}
            <div
              className={`ticket-section ${
                collapsedSections.ticketOverview ? "collapsed" : ""
              }`}
            >
              <div
                className="section-header collapsible-header"
                onClick={() => toggleSection("ticketOverview")}
              >
                <h3 className="section-title">
                  <span className="section-icon">📋</span>
                  Ticket Overview
                </h3>
              </div>
              <div className="ticket-overview-content">
                <div className="ticket-overview-grid">
                  <div className="overview-item">
                    <label>WTF Gym - {ticket.gym_name}</label>
                    <div className="overview-details">
                      <p>
                        <strong>Issue Type:</strong> {ticket.issue_type_name} →{" "}
                        {ticket.category_name} → {ticket.sub_category_name}
                      </p>
                      <p>
                        <strong>Raised by:</strong> {ticket.created_by_name}
                      </p>
                      <p>
                        <strong>Raised on:</strong> {ticket.created_date}
                      </p>
                    </div>
                  </div>

                  <div className="overview-item">
                    <label>
                      <span className="assigned-indicator">👤</span>
                      Assigned to: {ticket?.assigned_to_name || "Unassigned"}
                    </label>
                    <div className="overview-details">
                      <p>
                        <strong>Status:</strong> {ticket.status}
                      </p>
                      <p>
                        <strong>Has attached photos:</strong>{" "}
                        {ticket.imgarr?.length > 0 ? "Yes" : "No"}
                      </p>
                      <p>
                        <strong>Has attached videos:</strong>{" "}
                        {ticket.videoarr?.length > 0 ? "Yes" : "No"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="description-section">
                  <label>Description</label>
                  <p>{ticket.description}</p>
                </div>
              </div>
            </div>
            {/* Compact Progress */}
            <div className="compact-progress-section">
              <div className="compact-progress-content">
                <span className="progress-label">
                  📈 Progress: {ticket.progress_percentage || 0}%
                </span>
                <div className="compact-progress-bar">
                  <div
                    className="compact-progress-fill"
                    style={{ width: `${ticket.progress_percentage || 0}%` }}
                  ></div>
                </div>
                <span className="progress-status-mini">
                  {(ticket.progress_percentage || 0) === 0
                    ? "Not Started"
                    : (ticket.progress_percentage || 0) < 25
                    ? "Initial Phase"
                    : (ticket.progress_percentage || 0) < 50
                    ? "In Progress"
                    : (ticket.progress_percentage || 0) < 75
                    ? "Well Underway"
                    : (ticket.progress_percentage || 0) < 100
                    ? "Almost Complete"
                    : "Completed"}
                </span>
              </div>
            </div>
            {/* Attachments Section */}
            {(ticket.imgarr?.length > 0 || ticket.videoarr?.length > 0) && (
              <div className="ticket-section">
                <div
                  className="section-header collapsible-header"
                  onClick={() => toggleSection("attachments")}
                >
                  <h3 className="section-title">
                    <span className="section-icon">📎</span>
                    Attachments
                  </h3>
                </div>

                {!collapsedSections.attachments && (
                  <div className="attachments-container">
                    {/* Images */}
                    {ticket.imgarr?.length > 0 && (
                      <div className="attachment-type-section">
                        <h4 className="attachment-type-title">Images</h4>
                        <div className="attachment-grid">
                          {ticket.imgarr.map((img, index) => (
                            <div
                              key={img.uid || index}
                              className="attachment-item"
                            >
                              <div className="attachment-preview">
                                <img
                                  src={img.media_url}
                                  alt={`Attachment ${index + 1}`}
                                  className="attachment-image"
                                  onClick={() => openLightbox(img.media_url)}
                                />
                              </div>
                              <div className="attachment-actions">
                                <button
                                  onClick={() => openLightbox(img.media_url)}
                                  className="attachment-view-btn"
                                >
                                  <i className="fas fa-search-plus"></i> Zoom
                                  Image
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Videos */}
                    {ticket.videoarr?.length > 0 && (
                      <div className="attachment-type-section">
                        <h4 className="attachment-type-title">Videos</h4>
                        <div className="attachment-grid">
                          {ticket.videoarr.map((video, index) => (
                            <div
                              key={video.uid || index}
                              className="attachment-item video-item"
                            >
                              <div className="attachment-preview">
                                <video
                                  src={video.media_url}
                                  controls
                                  className="attachment-video"
                                />
                              </div>
                              <div className="attachment-actions">
                                <a
                                  href={video.media_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="attachment-download-btn"
                                >
                                  <i className="fas fa-download"></i> Download
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Work Completion Attachments Section */}
            {(ticket?.completed_imgarr?.length > 0 ||
              ticket?.completed_videoarr?.length > 0) && (
              <div className="ticket-section">
                <div
                  className="section-header collapsible-header"
                  onClick={() => toggleSection("workCompletionAttachments")}
                >
                  <h3 className="section-title">
                    <span className="section-icon">📸</span>
                    Work Completion Evidence
                  </h3>
                </div>

                {!collapsedSections.workCompletionAttachments && (
                  <div className="attachments-container">
                    {/* Completion Images */}
                    {ticket.completed_imgarr?.length > 0 && (
                      <div className="attachment-type-section">
                        <h4 className="attachment-type-title">
                          Completion Images
                        </h4>
                        <div className="attachment-grid">
                          {ticket.completed_imgarr.map((img, index) => (
                            <div
                              key={img.uid || index}
                              className="attachment-item"
                            >
                              <div className="attachment-preview">
                                <img
                                  src={img.media_url}
                                  alt={`Completion Image ${index + 1}`}
                                  className="attachment-image"
                                  onClick={() => openLightbox(img.media_url)}
                                />
                              </div>
                              <div className="attachment-actions">
                                <button
                                  onClick={() => openLightbox(img.media_url)}
                                  className="attachment-view-btn"
                                >
                                  <i className="fas fa-search-plus"></i> Zoom
                                  Image
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Completion Videos */}
                    {ticket.completed_videoarr?.length > 0 && (
                      <div className="attachment-type-section">
                        <h4 className="attachment-type-title">
                          Completion Videos
                        </h4>
                        <div className="attachment-grid">
                          {ticket.completed_videoarr.map((video, index) => (
                            <div
                              key={video.uid || index}
                              className="attachment-item video-item"
                            >
                              <div className="attachment-preview">
                                <video
                                  src={video.media_url}
                                  controls
                                  className="attachment-video"
                                />
                              </div>
                              <div className="attachment-actions">
                                <a
                                  href={video.media_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="attachment-download-btn"
                                >
                                  <i className="fas fa-download"></i> Download
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TAT Monitor Section */}
            {tatMetrics && (
              <div className="ticket-section">
                <div
                  className="section-header collapsible-header"
                  onClick={() => toggleSection("tatMonitor")}
                >
                  <h3 className="section-title">
                    <span className="section-icon">⏱️</span>
                    TAT Monitor - {ticket.ticket_no}
                  </h3>
                  {/* <p className="section-subtitle">
                    WTF Gym - {ticket.gymName} - {ticket.issueType} -{" "}
                    {ticket.category}
                  </p> */}
                </div>

                {!collapsedSections.tatMonitor && (
                  <div className="tat-metrics-grid">
                    <div className="tat-metric-card">
                      <div className="metric-icon total-tat">⏰</div>
                      <div className="metric-content">
                        <p className="metric-label">TOTAL TAT</p>
                        <p className="metric-value">
                          {tatMetrics.total_tat.value}
                        </p>
                        <span
                          className={`metric-status ${
                            tatMetrics.total_tat.within_sla
                              ? "within-sla"
                              : "over-sla"
                          }`}
                        >
                          {tatMetrics.total_tat.within_sla
                            ? "WITHIN SLA"
                            : "OVER SLA"}{" "}
                          ({tatMetrics.total_tat.sla})
                        </span>
                      </div>
                    </div>

                    <div className="tat-metric-card">
                      <div className="metric-icon time-assign">📅</div>
                      <div className="metric-content">
                        <p className="metric-label">TIME TO ASSIGN</p>
                        <p className="metric-value">
                          {tatMetrics.time_to_assign.value}
                        </p>
                        <span
                          className={`metric-status ${
                            tatMetrics.time_to_assign.within_sla
                              ? "within-sla"
                              : "over-sla"
                          }`}
                        >
                          {tatMetrics.time_to_assign.within_sla
                            ? "WITHIN SLA"
                            : "OVER SLA"}{" "}
                          ({tatMetrics.time_to_assign.sla})
                        </span>
                      </div>
                    </div>

                    <div className="tat-metric-card">
                      <div className="metric-icon work-duration">🔧</div>
                      <div className="metric-content">
                        <p className="metric-label">WORK DURATION</p>
                        <p className="metric-value">
                          {tatMetrics.work_duration.value}
                        </p>
                        <span
                          className={`metric-status ${
                            tatMetrics.work_duration.within_sla
                              ? "within-sla"
                              : "over-sla"
                          }`}
                        >
                          {tatMetrics.work_duration.within_sla
                            ? "WITHIN SLA"
                            : "OVER SLA"}{" "}
                          ({tatMetrics.work_duration.sla})
                        </span>
                      </div>
                    </div>

                    <div className="tat-metric-card">
                      <div className="metric-icon verification-delay">✅</div>
                      <div className="metric-content">
                        <p className="metric-label">VERIFICATION DELAY</p>
                        <p className="metric-value">
                          {tatMetrics.verification_delay.value}
                        </p>
                        <span
                          className={`metric-status ${
                            tatMetrics.verification_delay.within_sla
                              ? "within-sla"
                              : "over-sla"
                          }`}
                        >
                          {tatMetrics.verification_delay.within_sla
                            ? "WITHIN SLA"
                            : "OVER SLA"}{" "}
                          ({tatMetrics.verification_delay.sla})
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Resolution Details Section */}
            {resolutionDetails && ticket?.status === "work_completed" && (
              <div className="ticket-section">
                <div
                  className="section-header collapsible-header"
                  onClick={() => toggleSection("resolutionDetails")}
                >
                  <h3 className="section-title">
                    <span className="section-icon">🛠️</span>
                    Resolution Details
                  </h3>
                </div>

                {!collapsedSections.resolutionDetails && (
                  <div className="resolution-content">
                    <div className="resolution-item">
                      <label>Resolution Method:</label>
                      <span className="resolution-method">
                        {ticket?.method?.resolution_type}
                      </span>
                    </div>

                    <div className="resolution-item">
                      <label>Work Summary:</label>
                      <p>{resolutionDetails.work_completed_remarks}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Ticket Lifecycle Timeline */}
            <div className="ticket-section">
              <div
                className="section-header collapsible-header"
                onClick={() => toggleSection("timeline")}
              >
                <h3 className="section-title">
                  <span className="section-icon">📈</span>
                  Ticket Lifecycle Timeline
                </h3>
              </div>

              {!collapsedSections.timeline && (
                <div className="timeline-container">
                  {workflowLogs.map((log, index) => (
                    <div key={log.id} className="timeline-item">
                      <div className="timeline-marker">
                        <div
                          className="timeline-dot"
                          style={{ backgroundColor: log.statusColor }}
                        >
                          {index + 1}
                        </div>
                        {index < workflowLogs.length - 1 && (
                          <div className="timeline-line"></div>
                        )}
                      </div>

                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span
                            className={`timeline-status timeline-${log?.status
                              ?.toLowerCase()
                              .replace(/\s+/g, "-")}`}
                            style={{ backgroundColor: log.statusColor }}
                          >
                            {replaceUnderscores(log?.status)}
                          </span>
                          <span className="timeline-timestamp">
                            {formatDateTime(log?.timestamp)}
                          </span>
                        </div>

                        <div className="timeline-actor">
                          <span className="actor-icon">👤</span>
                          {log?.actor} ({log?.actorAccountType})
                        </div>

                        <div className="timeline-description">
                          {log?.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Verification Section */}
            {ticket.status === "work_completed" && isReceptionist && (
              <>
                <div
                  ref={verificationSectionRef}
                  className={`ticket-section verification-section ${
                    isHighlighted ? "verification-highlighted" : ""
                  }`}
                >
                  <div
                    className="section-header collapsible-header"
                    onClick={() => toggleSection("verification")}
                  >
                    <h3 className="section-title">
                      <span className="section-icon">⭐</span>
                      Mark as Resolved
                    </h3>
                  </div>

                  {!collapsedSections.verification && (
                    <>
                      <div className="verification-content">
                        <div className="rating-section">
                          <label>Rate the work quality (1-5 stars)</label>
                          <div className="star-rating">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span
                                key={star}
                                className={`star ${
                                  star <= rating ? "active" : ""
                                }`}
                                onClick={() => {
                                  setRating(star);
                                }}
                              >
                                ⭐
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="feedback-section">
                          <label>Verification Feedback</label>
                          <textarea
                            className="feedback-textarea"
                            placeholder="Provide feedback on the resolution quality..."
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            rows={4}
                            disabled={isSubmittingResolution}
                          />
                        </div>

                        {/* Phase 1: cascade resolve to related (child) tickets */}
                        <div
                          className="feedback-section"
                          style={{ display: "flex", alignItems: "center", gap: 8 }}
                        >
                          <input
                            type="checkbox"
                            id="resolveChildren"
                            checked={resolveChildren}
                            onChange={(e) => setResolveChildren(e.target.checked)}
                            disabled={isSubmittingResolution}
                          />
                          <label htmlFor="resolveChildren" style={{ margin: 0 }}>
                            Also resolve related (child) tickets, if any
                          </label>
                        </div>

                        {/* Resolution Proof Upload Section */}
                        <div className="upload-section">
                          <FileUploadComponent
                            maxFiles={3}
                            maxFileSize={50}
                            acceptedFormats={["jpg", "jpeg", "png", "mp4"]}
                            onFilesChange={handleResolutionFilesChange}
                            label="RESOLUTION PROOF IMAGE(S)/VIDEO"
                            hint="Upload photos or videos as proof of resolution verification"
                            required={true}
                            disabled={isSubmittingResolution}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="ticket-actions">
                        <div className="verification-actions">
                          <Button
                            variant="danger"
                            className="action-btn reject-btn"
                            onClick={() => handleTicketAction("rejected")}
                            disabled={
                              isSubmittingResolution ||
                              rating === 0 ||
                              feedback.trim() === "" ||
                              (resolutionFiles.images.length === 0 &&
                                resolutionFiles.videos.length === 0)
                            }
                          >
                            Reject Work
                          </Button>

                          <Button
                            variant="success"
                            className="action-btn verify-btn"
                            onClick={() => handleTicketAction("resolved")}
                            disabled={
                              isSubmittingResolution ||
                              rating === 0 ||
                              feedback.trim() === "" ||
                              (resolutionFiles.images.length === 0 &&
                                resolutionFiles.videos.length === 0)
                            }
                          >
                            {isSubmittingResolution ? (
                              <>
                                <div
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                ></div>
                                Verifying...
                              </>
                            ) : (
                              <>Mark as Resolved</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Manager Assignment Section */}
            {(() => {
              const assignmentStatus = getAssignmentStatus(ticket);
              const canAssignOrReassign =
                ticket.status === TICKET_OPENED ||
                ticket.status === TICKET_REOPENED ||
                (assignmentStatus.isReassignment &&
                  ![TICKET_CLOSED].includes(ticket.status));

              return canAssignOrReassign && isManager;
            })() && (
              <div className="ticket-section assignment-section">
                <div
                  className="section-header collapsible-header"
                  onClick={() => toggleSection("assignment")}
                >
                  <h3 className="section-title">
                    <span className="section-icon">👥</span>
                    {(() => {
                      const assignmentStatus = getAssignmentStatus(ticket);
                      return assignmentStatus.isReassignment
                        ? "Reassign Ticket to Supervisor"
                        : "Assign Ticket to Supervisor";
                    })()}
                  </h3>
                </div>

                {!collapsedSections.assignment && (
                  <div className="assignment-content">
                    <div className="assignment-form">
                      <label>
                        {(() => {
                          const assignmentStatus = getAssignmentStatus(ticket);
                          return assignmentStatus.isReassignment
                            ? "Reassignment Options"
                            : "Assignment Options";
                        })()}{" "}
                        <span className="text-danger">*</span>
                      </label>

                      {/* Assign to Myself Option */}
                      <div className="assignment-option mb-3">
                        <div className="assignment-option-header">
                          <input
                            type="radio"
                            id="assign-to-myself"
                            name="assignment-type"
                            value="myself"
                            checked={
                              selectedSupervisor ===
                              (userInfoData?.uid || userInfoData?.id)
                            }
                            onChange={() =>
                              setSelectedSupervisor(
                                userInfoData?.uid || userInfoData?.id
                              )
                            }
                            disabled={isAssigning}
                            className="me-2"
                          />
                          <label
                            htmlFor="assign-to-myself"
                            className="assignment-option-label"
                          >
                            🔄{" "}
                            {(() => {
                              const assignmentStatus =
                                getAssignmentStatus(ticket);
                              return assignmentStatus.isReassignment
                                ? "Reassign to Myself"
                                : "Assign to Myself";
                            })()}
                          </label>
                        </div>
                        <p className="assignment-option-description">
                          Take ownership of this ticket and handle it personally
                        </p>
                      </div>

                      {/* Assign to Supervisor Option */}
                      <div className="assignment-option">
                        <div className="assignment-option-header">
                          <input
                            type="radio"
                            id="assign-to-supervisor"
                            name="assignment-type"
                            value="supervisor"
                            checked={
                              selectedSupervisor !== "" &&
                              selectedSupervisor !==
                                (userInfoData?.uid || userInfoData?.id)
                            }
                            onChange={() => setSelectedSupervisor("")}
                            disabled={isAssigning}
                            className="me-2"
                          />
                          <label
                            htmlFor="assign-to-supervisor"
                            className="assignment-option-label"
                          >
                            👥{" "}
                            {(() => {
                              const assignmentStatus =
                                getAssignmentStatus(ticket);
                              return assignmentStatus.isReassignment
                                ? "Reassign to Different Supervisor"
                                : "Assign to Supervisor";
                            })()}
                          </label>
                        </div>
                        <p className="assignment-option-description">
                          {(() => {
                            const assignmentStatus =
                              getAssignmentStatus(ticket);
                            return assignmentStatus.isReassignment
                              ? "Change assignment to a different supervisor"
                              : "Delegate this ticket to one of your supervisors";
                          })()}
                        </p>

                        {/* Supervisor Dropdown - Only show when "Assign to Supervisor" is selected */}
                        {selectedSupervisor !==
                          (userInfoData?.uid || userInfoData?.id) && (
                          <div className="supervisor-selection mt-2">
                            <select
                              className="form-select"
                              value={
                                selectedSupervisor ===
                                (userInfoData?.uid || userInfoData?.id)
                                  ? ""
                                  : selectedSupervisor
                              }
                              onChange={(e) =>
                                setSelectedSupervisor(e.target.value)
                              }
                              disabled={isAssigning}
                            >
                              <option value="">Select a supervisor...</option>
                              {supervisors.map((supervisor) => (
                                <option
                                  key={supervisor.value}
                                  value={supervisor.value}
                                >
                                  {supervisor.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="assignment-actions">
                      <Button
                        variant="primary"
                        className="assign-ticket-btn"
                        onClick={handleAssignTicket}
                        disabled={isAssigning || !selectedSupervisor}
                      >
                        {isAssigning ? (
                          <>
                            <div
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            ></div>
                            {(() => {
                              const assignmentStatus =
                                getAssignmentStatus(ticket);
                              return assignmentStatus.isReassignment
                                ? "Reassigning..."
                                : "Assigning...";
                            })()}
                          </>
                        ) : (
                          <>
                            {(() => {
                              const assignmentStatus =
                                getAssignmentStatus(ticket);
                              return assignmentStatus.isReassignment
                                ? "Reassign Ticket"
                                : "Assign Ticket";
                            })()}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Manager Review and Verify Section */}
            {ticket.status === "resolved" && isManager && (
              <>
                <div
                  className={`ticket-section verification-section ${
                    isHighlighted ? "verification-highlighted" : ""
                  }`}
                >
                  <div
                    className="section-header collapsible-header"
                    onClick={() => toggleSection("managerVerification")}
                  >
                    <h3 className="section-title">
                      <span className="section-icon">🔍</span>
                      Review and Verify
                    </h3>
                  </div>

                  {!collapsedSections.managerVerification && (
                    <>
                      {/* Ticket Summary */}
                      <div className="verification-content">
                        <div className="ticket-summary-section">
                          <h6 className="section-subtitle">
                            Resolution Summary
                          </h6>
                          <div className="summary-details">
                            <div className="detail-row">
                              <span className="detail-label">Issue Type:</span>
                              <span className="detail-value">
                                {ticket?.issue_type_name} →{" "}
                                {ticket?.category_name} →{" "}
                                {ticket?.sub_category_name}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Priority:</span>
                              <span
                                className={`priority-badge priority-${ticket?.priority?.toLowerCase()}`}
                              >
                                {ticket?.priority?.toUpperCase()}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Description:</span>
                              <span className="detail-value">
                                {ticket?.description}
                              </span>
                            </div>
                            {ticket?.resolution_type && (
                              <div className="detail-row">
                                <span className="detail-label">
                                  Resolution Type:
                                </span>
                                <span className="detail-value">
                                  {ticket?.resolution_type}
                                </span>
                              </div>
                            )}
                            {ticket?.resolution_feedback && (
                              <div className="detail-row">
                                <span className="detail-label">
                                  Resolution Feedback:
                                </span>
                                <span className="detail-value">
                                  {ticket?.resolution_feedback}
                                </span>
                              </div>
                            )}
                            {ticket?.rating && (
                              <div className="detail-row">
                                <span className="detail-label">Rating:</span>
                                <span className="detail-value">
                                  {"⭐".repeat(parseInt(ticket?.rating))} (
                                  {ticket?.rating}/5)
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="feedback-section">
                          <label>
                            Verification Remarks{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className="feedback-textarea"
                            placeholder="Please provide your verification remarks..."
                            value={verifiedRemarks}
                            onChange={(e) => setVerifiedRemarks(e.target.value)}
                            rows={4}
                            disabled={isSubmittingVerification}
                          />
                          <small className="text-muted">
                            Provide detailed remarks about the quality of work
                            and resolution
                          </small>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="ticket-actions">
                        <div className="verification-actions">
                          <Button
                            variant="danger"
                            // className="action-btn reject-btn"
                            onClick={() =>
                              handleManagerVerification("rejected_by_mgr")
                            }
                            disabled={
                              isSubmittingVerification ||
                              !verifiedRemarks.trim()
                            }
                          >
                            Reject
                          </Button>

                          <Button
                            variant="success"
                            // className="action-btn verify-btn"
                            onClick={() =>
                              handleManagerVerification("verified_and_closed")
                            }
                            disabled={
                              isSubmittingVerification ||
                              !verifiedRemarks.trim()
                            }
                          >
                            {isSubmittingVerification ? (
                              <>
                                <div
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                ></div>
                                Verifying...
                              </>
                            ) : (
                              <>Verify & Close Ticket</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            <div className="align-center">
              <span className="status-indicator">
                Current Status: <strong>{ticket.status}</strong>
              </span>
              {/* <Button
                variant="outline-primary"
                size="sm"
                onClick={handleTimelineClick}
                className="timeline-btn"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginLeft: "auto",
                }}
              >
                <span style={{ fontSize: "14px" }}>📊</span>
                <span>View Timeline</span>
              </Button> */}
            </div>
          </div>
        )}
      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div
            className="lightbox-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="lightbox-close-btn"
              onClick={closeLightbox}
              aria-label="Close"
            >
              &#10005;
            </button>
            <img
              src={lightboxImage}
              alt="Enlarged view"
              className="lightbox-image"
            />
          </div>
        </div>
      )}

      {/* Timeline Modal */}
      <TicketLifecycleTimeline
        show={isTimelineModalOpen}
        onHide={handleTimelineModalClose}
        ticketId={ticket?.uid}
        ticketInfo={ticket}
      />
    </Modal>
  );
};

export default TicketDetailsModal;
